import { Socket } from 'socket.io-client';
import { Dispatch } from 'redux';
import { History } from 'history';
// actions
import dyopsAction from '../redux/actions/dyops';
import resultAction from '../redux/actions/result';
// types
import { dyopsType } from '../types/dyops';
// mapping
import { mapping } from '../config/mapping';
import { resultType } from '../types/result';
import { applyInfantModeToDyops, isInfantTestType } from './infantDefaults';
import { shouldChangeEyeAfterOvalFinish } from './ovalSession';

/** Emits dyops payload to socket and Redux. */
function emitDyopsUpdate(dispatch: Dispatch<any>, socket: Socket, payload: dyopsType): void {
  socket.emit('emitUpdateDoctor', { dyops: payload });
  dispatch(dyopsAction.getDyops(payload));
}

/** Commits oval refraction for the active eye and advances to the next eye or ends the test. */
function handleFinishOvalEye(
  history: History,
  dispatch: Dispatch<any>,
  socket: Socket,
  dyops: dyopsType,
  isIntegration: boolean,
  doctorName: string,
  isDark: boolean,
): void {
  const afterFinish = mapping.buildDyopsData(dyops, 'finishOvalEye');

  if (shouldChangeEyeAfterOvalFinish(afterFinish)) {
    const changePayload = mapping.buildDyopsData(afterFinish, 'changeEyeOval');
    emitDyopsUpdate(dispatch, socket, changePayload);
    return;
  }

  handleStopTest(history, dispatch, socket, afterFinish, isIntegration, doctorName, isDark);
}

export const handleDyopsControl = (
  dispatch: Dispatch<any>,
  socket: Socket,
  dyops: dyopsType,
  action: string,
  history: History,
  isIntegration: boolean,
  activeDyopRandom: () => string,
  doctorName: string,
  isDark?: boolean,
): void => {
  if (dyops.examMode === 'oval') {
    if (action === 'finishOvalEye') {
      handleFinishOvalEye(history, dispatch, socket, dyops, isIntegration, doctorName, isDark as boolean);
      return;
    }
    if (action === 'increase' || action === 'decrease' || action === 'notSure') {
      return;
    }
  }

  if (dyops.infantMode || isInfantTestType(dyops.result?.testType)) {
    if (action === 'stopTest') {
      handleStopTest(history, dispatch, socket, dyops, isIntegration, doctorName, isDark as boolean);
      return;
    }
    if (action === 'restart') {
      const payload = mapping.buildDyopsData(dyops, 'restart');
      emitDyopsUpdate(dispatch, socket, payload);
      return;
    }
    const payload = mapping.buildDyopsData(dyops, action);
    emitDyopsUpdate(dispatch, socket, payload);
    return;
  }

  // last 3 steps sequence
  const steps = dyops.steps.slice(dyops.steps.length - 3, dyops.steps.length).join('');
  // stop test steps sequence
  const stopSteps = steps === '++-' || steps === '-+-' || steps === '+-+';
  // stop one eye test condition
  const stopTestCondition = dyops.size === 104 || dyops.size === 4 || stopSteps;

  const newAction = stopTestCondition ? (dyops.closeLeftEye ? 'changeEye' : 'stopTest') : action;

  if (newAction === 'stopTest') {
    handleStopTest(history, dispatch, socket, dyops, isIntegration, doctorName, isDark as boolean);
  } else {
    // random active dyop when dyops size changes
    const active = newAction === 'increase' || newAction === 'decrease' ? activeDyopRandom() : dyops.active;

    const payload = mapping.buildDyopsData({ ...dyops, active }, newAction);

    emitDyopsUpdate(dispatch, socket, payload);
  }
};

/** Builds dyops payload with saved result fields applied for socket and Redux sync. */
function buildDyopsWithSavedResult(payload: dyopsType, savedResult: resultType): dyopsType {
  return {
    ...payload,
    examMode: savedResult.examMode ?? payload.examMode,
    chromaticPresetId: savedResult.chromaticPresetId ?? payload.chromaticPresetId,
    chromaticPrimary: savedResult.chromaticPrimary ?? payload.chromaticPrimary,
    chromaticSecondary: savedResult.chromaticSecondary ?? payload.chromaticSecondary,
    result: {
      ...payload.result,
      ...savedResult,
    },
  };
}

const handleStopTest = (
  history: History,
  dispatch: Dispatch<any>,
  socket: Socket,
  dyops: dyopsType,
  isIntegration: boolean,
  doctorName: string,
  isDark: boolean,
): void => {
  const roomId = socket.io.opts.query?.roomId ? socket.io.opts.query.roomId : '';
  const dyopsForStop =
    dyops.infantMode || isInfantTestType(dyops.result?.testType) ? applyInfantModeToDyops(dyops, 'infant') : dyops;
  const newDyops = {
    ...dyopsForStop,
    result: {
      ...dyopsForStop.result,
      doctorName: doctorName ? doctorName : '',
      date: Date.now(),
      roomId,
    },
  };

  const payload = mapping.buildDyopsData(newDyops, 'stopTest');

  // callback for patient redirect to results page
  const patientCallback = (_isIntegration: boolean, savedResult?: resultType) => {
    const nextDyops = savedResult ? buildDyopsWithSavedResult(payload, savedResult) : payload;
    dispatch(dyopsAction.getDyops(nextDyops));
    socket.emit('emitUpdateDoctor', { dyops: nextDyops });
  };

  // callback for restarting test and doctor redirect to results page
  const doctorCallback = (isIntegration: boolean, integrationPayload?: resultType): void => {
    // integration callback for iframe parent
    isIntegration && window?.top?.postMessage(JSON.stringify(integrationPayload), '*');

    const handleRestart = (dispatch: any, socket: any, dyops: dyopsType): void => {
      const payload = mapping.buildDyopsData(dyops, doctorName ? 'restart' : 'selfRestart');
      socket.emit('emitUpdateDoctor', {
        dyops: payload,
      });
      dispatch(dyopsAction.getDyops(payload));
    };

    !isIntegration && handleRestart(dispatch, socket, dyops);

    const routeToNavigate = isIntegration ? (doctorName ? '/connect' : '/login') : `/result/${roomId}`;
    const routeToNavigateDart = isIntegration ? '/end_test' : `/result/${roomId}`;
    history.push(isDark ? routeToNavigateDart : routeToNavigate);
  };

  dispatch(resultAction.editResultAction(roomId, payload, isIntegration, patientCallback, doctorCallback));
};
